import React from 'react';
import dayjs from 'lib/dayjs';
import { useRef, useState, memo } from 'react';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toWords } from 'number-to-words';

import { QRCode } from 'react-qr-code';
import { getDaysInMonth, getRoundingAdjustment } from 'utils/common';

const PayslipBody = memo(
  ({
    payslip,
    employee,
    net_pay,
    workPay,
    hra,
    sa,
    oa,
    overtime,
    paidLeave,
    incentives,
    gross_earnings,
    deductions,
    total_deductions,
    isGenerated,
    hideQrCode,
  }) => {
    const leaveDays = Number(payslip?.extra_leave_minutes / payslip?.standard_day_minutes).toFixed(
      1,
    );

    const totalDaysInMonth = getDaysInMonth(payslip?.year, payslip?.month);
    const totalCompanyHoliday = Number(
      payslip?.holiday_stats?.totalHolidayMinutes / payslip?.standard_day_minutes,
    ).toFixed(1);
    const WorkingDays = totalDaysInMonth - totalCompanyHoliday;

    return (
      <div className="w-full max-w-[900px] bg-white border-[1px] border-black text-xs sm:text-sm text-black box-border mx-auto relative z-10">
        {/* Header Section */}
        <div className="text-center border-b-2 border-black py-2">
          <div className="flex items-center justify-center px-3 gap-2">
            <h1 className="text-2xl font-bold uppercase tracking-wide">Qodors Technologies</h1>
          </div>
          <div className="mx-auto w-2/4 h-[2px] bg-black rounded my-2"></div>
          <p className="text-xs text-center mx-3">
            303, Shubh Square, Laldarwaja Main Rd, Lal Darwaja, Patel Nagar, Surat, Gujarat 395003
          </p>
        </div>

        {/* Payslip Month Title */}
        <div className="text-center bg-gray-200 font-bold border-b border-black py-1 px-3">
          Payslip for the Month of{' '}
          {dayjs(`${payslip?.year}-${payslip?.month}-1`).format('MMMM YYYY')}
        </div>

        {/* Employee Details Section */}
        <div className="border-b-2 border-black p-2 space-y-2">
          <h3 className="font-bold underline pb-3">Employee Pay Summary</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
            {/* Left Column */}
            <div className="grid grid-cols-[120px_8px_1fr] sm:grid-cols-[140px_10px_1fr] gap-y-1 py-1">
              <span>Employee Name</span>
              <span>:</span>
              <span className="font-semibold">
                {employee?.first_name + ' ' + employee?.last_name}
              </span>
              <span>Employee Id</span>
              <span className="text-center">:</span>
              <span>{employee?.employee_id}</span>
              <span>Designation</span>
              <span className="text-center">:</span>
              <span>{employee?.designation?.name}</span>
              <span>Date of Joining</span>
              <span className="text-center">:</span>
              <span>
                {employee?.joining_date
                  ? dayjs(employee?.joining_date).format('DD-MM-YYYY')
                  : 'N/A'}
              </span>
            </div>

            {/* Right Column */}
            <div className="grid grid-cols-[120px_8px_1fr] sm:grid-cols-[140px_10px_1fr] gap-y-1">
              <span>Pay Period</span>
              <span className="text-center">:</span>
              <span>{dayjs(`${payslip?.year}-${payslip?.month}-1`).format('MMMM, YYYY')}</span>
              <span>Pay Date</span>
              <span className="text-center">:</span>
              <span>
                {payslip?.calculated_at
                  ? dayjs(payslip.calculated_at).format('DD-MM-YYYY')
                  : 'Not Provided'}
              </span>
              <span>Total Working</span>
              <span className="text-center">:</span>
              <span>
                {WorkingDays} {WorkingDays > 1 ? 'Days' : 'Day'}
              </span>
              <span>Loss of Pay (LOP)</span>
              <span className="text-center">:</span>
              <span>
                {leaveDays} {leaveDays > 1 ? 'Days' : 'Day'}
              </span>
            </div>
          </div>
        </div>

        {/* Earnings & Deductions Table Header */}
        <div className="grid grid-cols-12 border-b border-black font-bold text-center bg-gray-200 divide-x divide-black items-stretch">
          <div className="col-span-6 px-2 py-1 text-left flex items-center">EARNINGS</div>
          <div className="col-span-6 px-2 py-1 text-right flex items-center justify-end">
            AMOUNT
          </div>
        </div>

        {/* Table Content Rows */}
        <div className="divide-y divide-black">
          <TableRow
            label="Basic Salary"
            amount={Number(workPay)?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          />
          <TableRow
            label="House Rent Allowance"
            amount={Number(hra)?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            isGenerated={isGenerated}
          />
          <TableRow
            label="Special Allowance"
            amount={Number(sa)?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            isGenerated={isGenerated}
          />
          <TableRow
            label="Other Allowance"
            amount={Number(oa)?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            isGenerated={isGenerated}
          />
          <TableRow
            label="Overtime"
            amount={Number(overtime)?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            isGenerated={isGenerated}
          />
          <TableRow
            label="Leave Encashment"
            amount={Number(paidLeave)?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            isGenerated={isGenerated}
          />
          {incentives?.map((incentive) => (
            <TableRow
              key={incentive?.id || Math.random()}
              label={incentive?.title}
              amount={Number(incentive?.amount)?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              isGenerated={isGenerated}
            />
          ))}

          {/* Gross / Total Row */}
          <div className="grid grid-cols-12 font-bold divide-x divide-black border-t-2 border-black items-stretch">
            <div className="col-span-6 px-2 py-1 flex items-center">Gross Earnings</div>
            <div className="col-span-6 px-2 py-1 text-right flex items-center justify-end">
              ₹
              {Number(gross_earnings)?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
        </div>

        {/* Deductions Section */}
        <div className="border-t-2 border-black">
          <div className="grid grid-cols-12 border-b border-black font-bold text-center bg-gray-200 divide-x divide-black items-stretch">
            <div className="col-span-6 px-2 py-1 text-left flex items-center">DEDUCTIONS</div>
            <div className="col-span-6 px-2 py-1 text-right flex items-center justify-end">
              AMOUNT
            </div>
          </div>

          <div className="divide-y divide-black">
            {payslip?.extra_leave_salary > 0 && (
              <TableRow
                label="Loss of Pay (LOP)"
                amount={Number(payslip?.extra_leave_salary)?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                isGenerated={isGenerated}
              />
            )}

            {deductions?.map((deduction) => (
              <TableRow
                key={deduction?.id || Math.random()}
                label={deduction?.title}
                amount={Number(deduction?.amount)?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                isGenerated={isGenerated}
              />
            ))}

            {/* Total Deductions Row */}
            <div className="grid grid-cols-12 font-bold divide-x divide-black border-t-2 border-black items-stretch">
              <div className="col-span-6 px-2 py-1 flex items-center">Total Deductions</div>
              <div className="col-span-6 px-2 py-1 text-right flex items-center justify-end">
                ₹
                {total_deductions?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Net Pay Section */}
        <div className="border-t-2 border-black">
          <div className="grid grid-cols-12 divide-x divide-black border-b bg-gray-200 border-black items-stretch">
            <div className="col-span-6 px-2 py-1 font-bold flex items-center">Net Pay</div>
            <div className="col-span-6 px-2 py-1 font-bold text-end flex items-center justify-end">
              AMOUNT
            </div>
          </div>

          <SummaryRow
            label="Gross Earnings"
            value={gross_earnings?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          />
          <SummaryRow
            label="Total Deductions"
            value={total_deductions?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          />

          <SummaryRow label="Rounding Adjustment" value={getRoundingAdjustment(net_pay)} />

          <div className="border-b border-black px-2 py-1 justify-between flex text-lg">
            <span className="font-bold whitespace-nowrap mr-2 ">Total Net Pay Amount</span>
            <span className="capitalize font-bold">₹{Math.round(net_pay).toLocaleString()}.00</span>
          </div>
          <div className="border-b border-black px-2 py-1 justify-between  flex">
            <span className="font-bold whitespace-nowrap mr-2">
              Total Net Pay Amount in Words :
            </span>
            <span className="capitalize">{toWords(Math.round(net_pay))} Only</span>
          </div>
        </div>

        {/* Footer Word Amount */}

        {/* Payment Details Table */}
        <div className="grid grid-cols-12 border-y border-black font-bold text-center bg-gray-200 divide-x divide-black items-stretch">
          <div className="col-span-12 px-2 py-1 text-left uppercase">PAYMENT DETAILS</div>
        </div>
        {payslip?.payment_mode && (
          <div className="">
            <PaymentRow
              label1="Mode of Payment"
              value1={
                payslip.payment_mode === 'BANK_TRANSFER'
                  ? 'Bank Transfer'
                  : payslip.payment_mode === 'CHEQUE'
                    ? 'Cheque'
                    : 'Cash'
              }
              label2="Bank Name"
              value2={payslip.bank_name || 'N/A'}
            />
            <PaymentRow
              label1="Account Holder"
              value1={payslip.account_holder_name || 'N/A'}
              label2="Account Number"
              value2={payslip.account_number || 'N/A'}
            />
            <PaymentRow
              label1="IFSC Code"
              value1={payslip.ifsc_code || 'N/A'}
              label2="Cheque No"
              value2={payslip.cheque_no || 'N/A'}
            />
          </div>
        )}
        {/* QR Code Verification */}
        {!hideQrCode && payslip?.hash_code && (
          <div className="border-t-2 border-black relative z-30">
            <div className="grid grid-cols-12 border-b border-black font-bold text-center bg-gray-200 divide-x divide-black items-stretch">
              <div className="col-span-12 px-2 py-1 text-left uppercase">VERIFICATION</div>
            </div>
            <div className="flex items-start justify-between px-4 py-3">
              <div className="space-y-4 pt-4 text-xs">
                <div>
                  <p className="font-semibold text-gray-700">Scan to Verify Payslip</p>
                  <p className="text-gray-500 leading-5">
                    Use your phone camera or any QR scanner to verify this payslip.
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Verification Code:</span>
                    <span className="font-semibold text-gray-800 tracking-wide">
                      {payslip.verification_code}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Verify at:</span>
                    <a
                      href="https://verify.qodors.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className=" text-blue-600 hover:underline"
                    >
                      verify.qodors.com
                    </a>
                  </div>
                </div>
              </div>
              <div className="bg-white p-1 ">
                <QRCode
                  value={payslip.verification_url}
                  size={140}
                  level="H"

                  bgColor="#ffffff"
                  fgColor="#000000"
                />
              </div>
            </div>
          </div>
        )}

        {/* Formula Disclaimer */}

        {/* Watermark only for display/capture if not profile/hide */}
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.08] pointer-events-none select-none z-20 overflow-hidden">
          <img
            src="/assets/images/qodors-desktop.png"
            alt="Watermark"
            className="w-3/4 object-contain -rotate-45"
          />
        </div>

        <div className="px-2 py-1 text-center text-[10px] sm:text-xs bg-gray-50 uppercase">
          **Total Net Payable = (Gross Earnings - Total Deductions) ± Rounding Adjustment
        </div>
      </div>
    );
  },
);

const Payslip = React.forwardRef(
  (
    { payslip, employee, onBack, isEmployeeProfile, hideControls, isGenerated, hideQrCode },
    ref,
  ) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const payslipRef = useRef(null);
    const captureRef = useRef(null);

    React.useImperativeHandle(ref, () => ({
      handleDownloadPDF,
    }));

    // Calculations
    const basicSalary = Number(payslip?.basic_salary) || 0;
    const hra = Number(payslip?.house_rent_allowance) || 0;
    const sa = Number(payslip?.special_allowance) || 0;
    const oa = Number(payslip?.other_allowance) || 0;
    const overtime = Number(payslip?.overtime_salary) || 0;
    const paidLeave = Number(payslip?.paid_leave_salary) || 0;

    let deductionsArr = [];
    let incentivesArr = [];

    if (payslip?.additional_entries?.length > 0) {
      deductionsArr = payslip.additional_entries.filter(
        (entry) => entry?.type === 'SUBTRACT' || entry?.type === 'Subtract',
      );
      incentivesArr = payslip.additional_entries.filter(
        (entry) => entry?.type === 'ADD' || entry?.type === 'Add',
      );
    }

    const total_incentives = incentivesArr.reduce((acc, inc) => acc + Number(inc?.amount), 0);
    const gross_earnings = basicSalary + hra + sa + oa + overtime + paidLeave + total_incentives;
    const total_other_deductions = deductionsArr.reduce((acc, ded) => acc + Number(ded?.amount), 0);
    const total_deductions =
      (Number(payslip?.extra_leave_salary) || 0) +
      (Number(payslip?.other_leave_salary) || 0) +
      total_other_deductions;
    const net_pay = gross_earnings - total_deductions;

    const contractual_gross = Number(payslip?.monthly_salary) || 0;

    // Props for body
    const bodyProps = {
      payslip,
      employee,
      isEmployeeProfile,
      hideControls,
      hideQrCode,
      net_pay,
      workPay: basicSalary,
      hra,
      sa,
      oa,
      overtime,
      paidLeave,
      incentives: incentivesArr,
      gross_earnings,
      contractual_gross,
      deductions: deductionsArr,
      total_other_deductions,
      total_deductions,
      isGenerated,
    };

    const handleDownloadPDF = async () => {
      if (!captureRef.current) return;
      setIsDownloading(true);

      try {
        const element = captureRef.current;

        const canvas = await html2canvas(element, {
          scale: window.devicePixelRatio * 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: 900,
          windowWidth: 900,
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const pdf = new jsPDF({
          orientation: 'p',
          unit: 'mm',
          format: 'a4',
          compress: true,
        });

        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
        pdf.save(`Payslip_${employee?.first_name}_${payslip?.month}_${payslip?.year}.pdf`);
      } catch (error) {
        console.error('Error generating PDF:', error);
      } finally {
        setIsDownloading(false);
      }
    };

    return (
      <div
        className={`${isEmployeeProfile || hideControls ? '' : 'min-h-screen'} bg-gray-100 p-4 sm:p-10 flex flex-col items-center relative`}
      >
        {/* Control Bar */}
        {!isEmployeeProfile && !hideControls && (
          <div className="w-full max-w-[900px] mb-6 flex justify-between items-center sticky top-0 z-10 bg-gray-100/80 backdrop-blur-md py-4 rounded-xl px-4">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-all shadow-sm"
            >
              <ArrowLeft size={18} />
              <span className="font-medium">Back</span>
            </button>

            <button
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              className="flex items-center space-x-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all shadow-sm"
            >
              {isDownloading ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <Download size={18} />
              )}
              <span className="font-medium">
                {isDownloading ? 'Generating...' : 'Download PDF'}
              </span>
            </button>
          </div>
        )}

        {/* Main Display Container */}
        <div
          ref={payslipRef}
          className={`w-full px-4 sm:px-8 py-${isEmployeeProfile || hideControls ? '4' : '16'} max-w-[900px] mx-auto relative overflow-x-auto`}
        >
          <PayslipBody {...bodyProps} />
          <p className="text-center text-gray-500 text-xs mt-4">
            Note: This is a computer-generated payslip and does not require a stamp or signature.
          </p>
        </div>

        {/* Hidden Capture Container */}
        <div
          ref={captureRef}
          style={{
            position: 'fixed',
            top: 0,
            left: '-10000px',
            width: '900px',
            zIndex: -1,
            pointerEvents: 'none',
            backgroundColor: '#ffffff',
            visibility: 'visible',
          }}
        >
          <div style={{ padding: '40px', width: '900px' }}>
            <PayslipBody {...bodyProps} hideControls={true} />
            <p className="text-center text-gray-500 text-xs mt-4">
              Note: This is a computer-generated payslip and does not require a stamp or signature.
            </p>
          </div>
        </div>
      </div>
    );
  },
);

// Helper Components
const TableRow = ({ label, amount }) => {
  return (
    <div className="grid grid-cols-12 divide-x divide-black items-stretch">
      <div className="col-span-6 px-2 py-1 flex items-center">{label}</div>
      <div className="col-span-6 px-2 py-1 text-right flex items-center justify-end">{amount}</div>
    </div>
  );
};

const SummaryRow = ({ label, value }) => (
  <div className="grid grid-cols-12 divide-x divide-black border-b border-black items-stretch leading-none min-h-[32px]">
    <div className="col-span-6 px-2 py-1 flex items-center">{label}</div>
    <div className="col-span-6 px-2 py-1 flex items-center justify-end text-right">{value}</div>
  </div>
);
const PaymentRow = ({ label1, value1, label2, value2 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-12 divide-y sm:divide-y-0 sm:divide-x divide-black border-b border-black items-stretch leading-none min-h-[32px]">
    <div className="sm:col-span-3 px-2 py-1 flex items-center font-bold text-[10px] sm:text-xs">
      {label1}
    </div>
    <div className="sm:col-span-3 px-2 py-1 flex items-center text-[10px] sm:text-xs break-words">
      {value1}
    </div>
    <div className="sm:col-span-3 px-2 py-1 flex items-center font-bold text-[10px] sm:text-xs">
      {label2}
    </div>
    <div className="sm:col-span-3 px-2 py-1 flex items-center text-[10px] sm:text-xs break-words">
      {value2}
    </div>
  </div>
);

export default Payslip;
